<script lang="ts">
	import { datadogRum } from '@datadog/browser-rum';
	import { afterNavigate, beforeNavigate, goto } from '$app/navigation';
	import { page } from '$app/state';
	import BigButton from '$lib/components/BigButton.svelte';
	import Breadcrumbs from '$lib/components/Breadcrumbs.svelte';
	import CategoryButton from '$lib/components/CategoryButton.svelte';
	import Filters from '$lib/components/filters/Filters.svelte';
	import FiltersSelected from '$lib/components/filters/FiltersSelected.svelte';
	import SortOptions from '$lib/components/filters/SortOptions.svelte';
	import FilterProvider, { CategoriesSortOptions } from '$lib/components/filters/store.svelte';
	import Product from '$lib/components/product/Product.svelte';
	import ProgressBar from '$lib/components/ProgressBar.svelte';
	import ScrollToTop from '$lib/components/ScrollToTop.svelte';
	import Spinner from '$lib/components/Spinner.svelte';
	import ThumbnailImage from '$lib/components/ThumbnailImage.svelte';
	import Title from '$lib/components/Title.svelte';
	import { m } from '$lib/paraglide/messages.js';
	import { setContext, tick, untrack } from 'svelte';

	import type { Snapshot } from '../$types';

	import { type CategoryPageData, fetchContent } from './page.category';

	datadogRum.setViewName('/category');

	let {
		data,
		store,
		captureSnapshot = $bindable(),
		restoreSnapshot = $bindable(),
	}: {
		data: CategoryPageData;
		store: App.PageData['store'];
		captureSnapshot?: Snapshot['capture'];
		restoreSnapshot?: Snapshot['restore'];
	} = $props();

	let moreProducts: ProductFromCategory[] = $state([]);
	let prefetchedProducts: ProductFromCategory[] | null = $state(null);
	let isPrefetching = $state(false);
	const products = $derived([...(data?.products ?? []), ...moreProducts]);
	const productToLoadCount = $derived(
		Math.min(Math.abs(products.length - (data.count ? parseInt(data.count) : 0)), data.pageSize),
	);

	let currentSort = $derived(
		(page.url.searchParams.get('sort') as CategoriesSortOptions) ?? CategoriesSortOptions.Relevance,
	);

	afterNavigate((navigation) => {
		if (navigation.type === 'popstate') return;
		if (navigation.from?.params?.slug !== navigation.to?.params?.slug) {
			moreProducts = [];
		}
	});

	captureSnapshot = () => ({ moreProducts, prefetchedProducts, scroll: window.scrollY });
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	restoreSnapshot = async (v: any) => {
		if (!v) return;

		moreProducts = v.moreProducts;
		prefetchedProducts = v.prefetchedProducts ?? null;
		await tick();
		if (v.scroll) {
			window.scrollTo(0, v.scroll);
		}
	};

	$effect(() => {
		const searchParams = untrack(() => page.url.searchParams);
		if (
			(!searchParams.has('sort') && currentSort === CategoriesSortOptions.Relevance) ||
			searchParams.get('sort') === currentSort
		) {
			return;
		}

		const params = new URLSearchParams(searchParams);

		if (currentSort === 'relevance') {
			params.delete('sort');
		} else {
			params.set('sort', currentSort);
		}

		goto(`?${params}`);
	});

	const filterProvider = new FilterProvider();
	setContext('filter', filterProvider);

	// Clear prefetch buffer when navigating to a different category
	afterNavigate((navigation) => {
		if (navigation.type === 'popstate') return;
		if (navigation.from?.params?.slug !== navigation.to?.params?.slug) {
			prefetchedProducts = null;
		}
	});

	function showNextPage() {
		if (!prefetchedProducts?.length) return;

		moreProducts = [...moreProducts, ...prefetchedProducts];
		prefetchedProducts = null;
	}

	$effect(() => {
		filterProvider.update(data.filters);
		moreProducts = [];
		prefetchedProducts = null;
		isPrefetching = false;
	});

	$effect(() => {
		if (isPrefetching || prefetchedProducts !== null || products.length === Number(data.count)) {
			return;
		}

		isPrefetching = true;

		fetchContent(
			store.store_id,
			new URLSearchParams({
				...Object.fromEntries(page.url.searchParams),
				slug: data.slug,
				from: products.length.toString(),
				size: data.pageSize.toString(),
				is_brand: String(data.isBrand),
			}),
			window.fetch,
		).then((response) => {
			prefetchedProducts = response?.products ?? [];
			isPrefetching = false;
		});
	});

	beforeNavigate(async (navigation) => {
		if (navigation.type !== 'link' || navigation.to?.route.id !== '/[...slug]') return;

		navigation.cancel();
		await goto(navigation.to?.url.pathname, {
			state: {
				breadcrumbs: data.category!.breadcrumbs,
			},
		});
	});
</script>

<div class="full-container mx-auto">
	<Breadcrumbs breadcrumbs={data.category!.breadcrumbs} class="mb-4" />

	{#if data.category?.thumbnail_categories?.length}
		{#if data?.category?.h1_title}
			<Title>
				{data.category.h1_title}
			</Title>
		{/if}
		{#if data.category.header_description}
			<div class="prose-sm prose-cms-content">
				{@html data.category.header_description}
			</div>
		{/if}
		<div class="mt-5 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
			{#each data.category.thumbnail_categories as thumbnail_category (thumbnail_category.slug)}
				<CategoryButton
					href="/{thumbnail_category.slug}"
					class="w-full rounded"
					scaleOnHover
					text={thumbnail_category.name}
				>
					<ThumbnailImage
						src="category/{thumbnail_category.image}"
						alt={thumbnail_category.name}
						defaultSize={300}
						maxSize={1200}
					/>
				</CategoryButton>
			{/each}
		</div>

		{#if data.category.footer_description}
			<div class="prose-sm prose-cms-content mt-5">
				{@html data.category.footer_description}
			</div>
		{/if}
	{:else}
		<div class="grid grid-cols-1 gap-x-6 gap-y-2 xl:grid-cols-[1fr_4fr]">
			<div class="hidden xl:block">
				<Filters filters={data.filters} productCount={Number(data.count)} />
			</div>
			<div class="order-1 space-y-2 xl:order-2">
				<Title>
					{data.category?.h1_title || data.category?.name}
				</Title>
				{#if data.category?.header_description}
					<div class="prose prose-cms-content hidden pb-3 xl:block">
						{@html data.category.header_description}
					</div>
				{/if}
				<div class="flex gap-3 xl:hidden">
					<div class="flex-1">
						<Filters filters={data.filters} productCount={Number(data.count)} />
					</div>
					<div class="flex-1">
						<SortOptions bind:currentSort />
					</div>
				</div>
				<FiltersSelected />

				<div class="flex items-center justify-between">
					<div>{m['product_count']({ count: Number(data.count) })}</div>
					<div class="hidden items-center gap-2 xl:flex">
						<span class="hidden font-medium whitespace-nowrap xl:block">{m['sort_by']()}</span>
						<SortOptions bind:currentSort />
					</div>
				</div>

				<div
					class="grid grid-cols-2 content-start justify-around gap-6 md:grid-cols-3 lg:grid-cols-4"
				>
					{#each products ?? [] as product, i (i)}
						<Product {product} />
					{/each}
				</div>

				<div class="flex w-full flex-col items-center pt-16 not-last:pb-16">
					{m['pagination.product_visible_count']({
						current_product_count: products.length,
						total_product_count: parseInt(data.count ?? '0'),
					})}

					{#if productToLoadCount}
						<ProgressBar
							count={products.length}
							total={parseInt(data.count ?? '0')}
							class="max-w-2xs"
						/>
						<BigButton
							onclick={showNextPage}
							class="w-full sm:w-1/3"
							disabled={!prefetchedProducts}
						>
							<div class="flex items-center gap-2">
								<span>{m['pagination.show_more_products']()}</span>
								{#if !prefetchedProducts}
									<Spinner />
								{/if}
							</div>
						</BigButton>
					{/if}
				</div>
				{#if data.category?.footer_description}
					<div class="prose-sm prose-cms-content">
						{@html data.category?.footer_description}
					</div>
				{/if}
			</div>
		</div>
	{/if}

	<ScrollToTop />
</div>
